import { GMI, STATE } from "../../../constants";
import { types } from "../../../utils/js/types";
import { batchActions } from "../../../utils/redux";

const { TRIPS } = STATE.UI;

/**
 * @typedef {"confirmationNumber" | "firstName" | "lastName"} FieldNames
 */

/** @type {Record<FieldNames, string[]>} */
const FIELD_PATHS = {
  confirmationNumber: TRIPS.RETRIEVE.CONFIRMATION_NUMBER,
  firstName: TRIPS.RETRIEVE.FIRST_NAME,
  lastName: TRIPS.RETRIEVE.LAST_NAME
};

/**
 * Clears a field inside gmi.ui.trips.retrieve[fieldName]
 * @private
 * @param {FieldNames} field
 * @returns {Object} {type: string, path: [], value: null}
 */
function clearField(field) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value: null
  };
}

/**
 * Unsetter for the gmi.ui.trips.retrieve data
 * @private
 * @returns {Object} Unsetter action object
 */
function clearState() {
  const batchedActions = [];

  Object.keys(FIELD_PATHS).forEach(field => {
    batchedActions.push(clearField(field));
  });
  
  return batchActions(...batchedActions);
}

/**
 * Sets a field inside follow up state
 * @private
 * @param {FieldNames} field
 * @param {unknown} value
 * @returns {Object} {type: string, path: [], value: object}
 */
function setField(field, value) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value
  };
}

/**
 * Setter for the ui.trips.retrieve data
 * @private
 * @param {Record<FieldNames, unknown>} fields - Key-value pairs of fields and values to set
 * @returns {Object} Batched action that sets each passed field
 */
const setState = (fields = {}) => {
  const batchedActions = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (FIELD_PATHS[key] && value ) {
      batchedActions.push(setField(key, value));
    }
  });
  
  return batchActions(...batchedActions);
};

export const state = {
  /**
   * @name set
   * @desc A global setter for ui.trips.retrieve
   * @param {FieldNames | null} [field]
   * @param {Object} [args] - Value to set
   * @returns {Function} Dispatch function
   * @memberof actions.trips
   * @example
   *  dispatch(actions.trips.retrieve.state.set('confirmationNumber', '1400217967'));
   *  dispatch(actions.trips.retrieve.state.set('firstName', 'DOOGIE'));
   *  dispatch(actions.trips.retrieve.state.set('lastName', 'HOWSER'));
   */
  set(field = null, value = {}) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(setState(value));
      if (FIELD_PATHS[field]) return dispatch(setField(field, value));
    };
  },
  
  /**
   * @name unset
   * @desc A global unsetter for ui.trips.retrieve data
   * @param {FieldNames | null} [field]
   * @returns {Function} Dispatch function
   * @memberof actions.trips
   * @example
   *  store.dispatch(actions.trips.retrieve.state.unset('confirmationNumber'))
   *  store.dispatch(actions.trips.retrieve.state.unset())
   */
  unset(field = null) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(clearState());
      if (FIELD_PATHS[field]) return dispatch(clearField(field));
    };
  }
};
